
import Vue from 'vue'
import { Component } from 'vue-property-decorator'
import { Mutation } from 'vuex-class'

import ErrorHandler from '@/services/ErrorHandlerService'
import { INotificationService } from '@/services/NotificationService'
import AuthenticationService from '@/services/AuthenticationService'
import CardTitle from '../components/Atomic/CardTitle.vue'
import GreenButton from '../components/Atomic/GreenButton.vue'
import ProgressLinear from '../components/Atomic/ProgressLinear.vue'

@Component({
  components: {
    CardTitle,
    GreenButton,
    ProgressLinear,
  },
})
export default class Login extends Vue {
  @Mutation private setLogged: any
  @Mutation private setRefreshToken: any
  @Mutation private setAccessToken: any
  @Mutation private setUser: any

  private show: boolean = false
  private isPending: boolean = false
  private isPendingOfficeLoginUrl: boolean = false
  private username: string = ''
  private password: string = ''
  private officeLoginUrl: any = ''
  private authorizationCode: string = ''

  private errorHandler = new ErrorHandler()
  private $notificationService!: INotificationService

  public async loginWithUsernameAndPassword() {
    this.isPending = true

    const validated = await this.$validator.validateAll()
    if (validated) {
      try {
        const response = await AuthenticationService.authenticate(
          this.username,
          this.password,
        )
        const data = response.data
        this.setUserDataAndTokens(data)
      } catch (error: any) {
        this.errorHandler.handle(this, error.response.data.code, true)
      } finally {
        this.isPending = false
      }
    }
    this.isPending = false
  }

  private created() {
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')
    if (code) {
      this.authorizationCode = code
      this.loginWithAuthorizationCode()
    } else {
      this.fetchOfficeLoginUrl()
    }
  }

  private mounted() {
    if (this.$router.currentRoute.query['action'] === 'expired') {
      this.$notificationService.error(this.$t('LOGIN.TokenExpired').toString())
    }
  }

  private async fetchOfficeLoginUrl() {
    try {
      this.isPendingOfficeLoginUrl = true
      this.officeLoginUrl = await AuthenticationService.fetchOfficeLoginUrl()
    } catch (error: any) {
      this.errorHandler.handle(this, error.response.data.code, true)
    } finally {
      this.isPendingOfficeLoginUrl = false
    }
  }

  private loginWithOffice() {
   this.isPending = true
  }

  private async loginWithAuthorizationCode() {
    try {
      this.isPending = true
      const response = await AuthenticationService.authenticateWithAuthorizationCode(
        this.authorizationCode,
      )
      const data = response.data
      this.setUserDataAndTokens(data)
    } catch (error: any) {
      this.errorHandler.handle(this, error.response.data.code, true)
      this.authorizationCode = ''
      await this.fetchOfficeLoginUrl()
    } finally {
      this.isPending = false
    }
  }

  private setUserDataAndTokens(userDataAndTokens: any) {
    const user = userDataAndTokens.user
    const tokens = userDataAndTokens.tokens
    this.setUserData(user)
    this.setUserTokens(tokens)
    if (user.mustChangePassword) {
      this.$notificationService.info(
        this.$t('LOGIN.ChangePassword').toString(),
      )
      // Push the ChangePassword component with the password (hides the oldPassword input)
      this.$router.push({
        name: 'change-password',
        params: { username: this.username, loggedInPassword: this.password },
      })
    } else {
      this.setLogged(true)
      this.$notificationService.success(this.$t('LOGIN.LoggedIn').toString())
      if (user.roles.includes('Enseignant')) {
        this.$router.push('/users')
      } else if (user.roles.some( (role) => ['cipeo_recherche', 'support', 'ingenierie'].includes(role.toLowerCase()))) {
        this.$router.push('/cipeo')
      } else {
        this.$router.push('/')
      }
    }
  }

  private setUserData(userData: any) {
    this.setUser(userData)
  }

  private setUserTokens(userTokens: any) {
    this.setRefreshToken(userTokens.refresh)
    this.setAccessToken(userTokens.access)
    AuthenticationService.setToken(userTokens.access)
  }

  private recoverUsername() {
    this.$router.push('/recover-username')
  }

  private recoverPassword() {
    this.$router.push('/recover-password')
  }
}
